import { userApi } from '../redux/api/axiosInstance';

export interface HRLocalization {
  countryCode?: string;
  defaultCurrency: string;
  defaultPayrollFrequency: string;
  fiscalYearStart: string;
}

export interface HRFieldDefinition {
  fieldKey: string;
  label: string;
  /**
   * The country-package provider emits `text | number | boolean | select | date`
   * (see HRFieldDefinition.cs in User Management); `toggle` is an older accepted alias for
   * `boolean`. The rest of this union — `longtext | currency | daterange | time | duration |
   * multiselect | checkbox | employee | department | computed` — is UM's separate
   * field-definition EAV allowlist (migration 146, `Constants/FieldDefinitionTypes.cs`), reached
   * through this same shape via `fieldDefinitionLogic.toRenderableField` (docs/65 section 3.3),
   * because `DynamicField` renders both without a second control set. A string outside this
   * whole union still reaches the component (it is not narrowed at the network boundary) and
   * renders visibly as unsupported rather than silently falling back to a text input — see
   * `isSupportedFieldType` in `services/AdminServices/RequestPolicyServices/fieldDefinitionService.ts`.
   */
  fieldType:
    | 'text' | 'date' | 'select' | 'number' | 'boolean' | 'toggle' | 'file'
    | 'longtext' | 'currency' | 'daterange' | 'time' | 'duration' | 'multiselect'
    | 'checkbox' | 'employee' | 'department' | 'computed';
  isRequired?: boolean;
  /**
   * The wire name is `validationRegex` (C# `HRFieldDefinition.ValidationRegex`).
   * `validationPattern` was the name this type declared originally and no payload
   * has ever carried it — kept only so nothing that reads it breaks.
   */
  validationRegex?: string | null;
  validationPattern?: string | null;
  selectOptions?: string[];
  groupName?: string;
  displayOrder: number;
  /**
   * Where this field's VALUE lives — the catalogue is the single authority, so no client keeps
   * its own list of which keys are statutory.
   *
   * Falsy (the default, and what every older backend sends) means `EmployeeFieldValues`, reached
   * via `getEmployeeFieldValues` / `saveEmployeeFieldValues`. True means `EmployeeStatutoryIds`,
   * reached via `employeeStatutoryIdService`, keyed by (employee, idType) with idType === fieldKey.
   *
   * This matters because Payroll builds the UAE WPS/SIF file from `EmployeeStatutoryIds` ALONE.
   * A value typed into the generic country-field form used to land in `EmployeeFieldValues`, where
   * the filing could not see it — on HRMS_DEV two employees had an Emirates ID recorded that way
   * with no statutory row at all.
   */
  isStatutoryId?: boolean;
  /**
   * Removed 2026-08-26 (docs/67-UNIFIED-POLICY.md section 11.3 finding 4, section 16.12j):
   * this type used to carry an optional `condition: { fieldKey, value }` clause for
   * client-side conditional field visibility. No backend in the estate ever emitted it -
   * migration 146 added only ScopeKind/ScopeId/RequestModuleName to
   * EmployeeFieldDefinitions, and UM's CountryPackages/Models/HRFieldDefinition.cs has no
   * Condition field. It was a client-only stub with no producer on the wire, deleted rather
   * than kept as a dead prop. Re-add only against a real backend-emitted condition shape.
   */
}

export interface FieldValue {
  fieldKey: string;
  fieldValue: string;
  updatedAt: string;
}

export const getLocalization = async (countryCode?: string): Promise<HRLocalization> => {
  const params = countryCode ? `?countryCode=${countryCode}` : '';
  const { data } = await userApi.get(`country-metadata/localization${params}`);
  return data;
};

export const getEmployeeFields = async (countryCode?: string): Promise<HRFieldDefinition[]> => {
  const params = countryCode ? `?countryCode=${countryCode}` : '';
  const { data } = await userApi.get(`country-metadata/employee-fields${params}`);
  return data;
};

export const getOrgFields = async (countryCode?: string): Promise<HRFieldDefinition[]> => {
  const params = countryCode ? `?countryCode=${countryCode}` : '';
  const { data } = await userApi.get(`country-metadata/org-fields${params}`);
  return data;
};

export const getEmployeeFieldValues = async (employeeId: number): Promise<FieldValue[]> => {
  const { data } = await userApi.get(`country-field-values/employee/${employeeId}`);
  return data;
};

/**
 * The server's response to a country-field save.
 *
 * `warnings` carries compliance problems the server detected but deliberately did NOT block on —
 * the value is already persisted by the time the check runs ("warn, don't fail", the same posture
 * as the WPS export). The UAE probation cap is the live example: UM compares
 * `EMP_PROBATION_END_DATE` against hire date + 6 months and returns a sentence when it is exceeded.
 */
export interface SaveFieldValuesResult {
  warnings: string[];
}

/**
 * Save an employee's country-package field values.
 *
 * **This used to return `Promise<void>` and discard the response body**, so every warning the
 * server computed was thrown away and the only caller toasted unconditional success. A UAE
 * probation-cap breach — a labour-law violation under FDL 33/2021 Art. 11, which caps probation at
 * six months with no extension and no restart with the same employer — was therefore invisible to
 * every user in the product. The warnings are returned now; the save still succeeds either way.
 */
export const saveEmployeeFieldValues = async (
  employeeId: number,
  values: Record<string, string>,
): Promise<SaveFieldValuesResult> => {
  const { data } = await userApi.put(`country-field-values/employee/${employeeId}`, values);
  // Tolerates an older server that returns no body: no warnings is a legitimate answer, and a
  // missing field must never read as "something is wrong".
  return { warnings: Array.isArray(data?.warnings) ? data.warnings : [] };
};

export interface EmployeeFieldValueRow {
  employeeId: number;
  employeeName: string;
  departmentName?: string | null;
  fieldValue?: string | null;
}

export const getEmployeesByField = async (fieldKey: string): Promise<EmployeeFieldValueRow[]> => {
  const { data } = await userApi.get(`country-field-values/by-field/${fieldKey}`);
  return data;
};

export const getOrgFieldValues = async (orgId: number): Promise<FieldValue[]> => {
  const { data } = await userApi.get(`country-field-values/org/${orgId}`);
  return data;
};

export const saveOrgFieldValues = async (
  orgId: number,
  values: Record<string, string>,
): Promise<void> => {
  await userApi.put(`country-field-values/org/${orgId}`, values);
};
